/**
 * Custom hook for fetching order details with SWR
 * Falls back to fetch API if SWR is not available
 */

import { useState, useEffect } from 'react';
import { OrderDetailsDTO } from '../types/order';
import { API_BASE } from '../config/api';

interface UseOrderDetailsOptions {
  orderNumber: string;
  enabled?: boolean;
}

interface UseOrderDetailsReturn {
  order: OrderDetailsDTO | null;
  loading: boolean;
  error: string | null;
  mutate: () => Promise<void>;
}

export const useOrderDetails = (
  { orderNumber, enabled = true }: UseOrderDetailsOptions
): UseOrderDetailsReturn => {
  const [order, setOrder] = useState<OrderDetailsDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const fetchOrder = async () => {
    if (!enabled || !orderNumber) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication required. Please log in again.');
      }
      
      const response = await fetch(`${API_BASE}/api/orders/${orderNumber}?format=dto`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Order not found');
        }
        if (response.status === 403) {
          throw new Error('Access denied');
        }
        throw new Error(`Failed to fetch order: ${response.statusText}`);
      }
      
      const data: OrderDetailsDTO = await response.json();
      setOrder(data);
    } catch (_err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch order details');
      setOrder(null);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchOrder();
    
    // Refetch on focus (basic SWR-like behavior)
    const handleFocus = () => {
      if (enabled && orderNumber) {
        fetchOrder();
      }
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [orderNumber, enabled]);
  
  return {
    order,
    loading,
    error,
    mutate: fetchOrder,
  };
};

